import React, { useState } from 'react';
import { ArrowRight, MessageSquare, BarChart2, Shield, Users, Phone, Brain, Clock } from 'lucide-react';
import { Logo } from '../components/Logo';
import { images } from '../assets/images';
import { SEOHelmet } from '../components/SEOHelmet';
import { ContactForm } from '../components/ContactForm';
import { ThemeToggle } from '../components/ThemeToggle';

export function LandingPage() {
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<'demo' | 'getStarted'>('getStarted');

  const handleFormOpen = (type: 'demo' | 'getStarted') => {
    setFormType(type);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHelmet />
      
      {/* Navigation */}
      <nav className="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <Logo className="h-14" />
            <div className="flex items-center gap-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">Benefits</a>
              <ThemeToggle />
              <div className="flex items-center gap-3">
                <a 
                  href="https://dashboard.dealerdial.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border border-gray-300 dark:border-gray-700"
                >
                  Sign In
                </a>
                <button
                  onClick={() => handleFormOpen('demo')}
                  className="text-primary border-2 border-primary px-4 py-2 rounded-lg hover:bg-primary hover:text-white transition-colors dark:text-primary-light dark:border-primary-light dark:hover:bg-primary-light dark:hover:text-white"
                >
                  Demo
                </button>
                <button
                  onClick={() => handleFormOpen('getStarted')}
                  className="bg-gradient-to-r from-primary to-primary-dark text-white px-4 py-2 rounded-lg hover:from-primary-dark hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-radial from-white via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(217,28,31,0.15),transparent_50%)]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="z-10">
              <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-6">
                AI Voice Agents for Auto Dealerships
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Deploy intelligent AI voice agents that sound human to reconnect with your previous customers, drive repeat business, and boost service revenue - all automatically.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => handleFormOpen('getStarted')}
                  className="bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-3 rounded-lg hover:from-primary-dark hover:to-primary transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                >
                  Get Started
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => handleFormOpen('demo')}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-2 border-primary dark:border-primary-light text-primary dark:text-primary-light px-6 py-3 rounded-lg hover:bg-primary hover:text-white dark:hover:bg-primary-light dark:hover:text-white transition-colors shadow-md hover:shadow-lg"
                >
                  Request Demo
                </button>
              </div>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent dark:from-primary-dark/20 dark:to-transparent"></div>
              <img 
                src={images.hero}
                alt="Car dealership showroom" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Next-Generation Customer Outreach
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Powered by advanced AI, our voice agents engage your customers in natural, meaningful conversations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: "Advanced AI Voice Agents",
                description: "Human-like conversational AI powered by the latest LLM technology for natural, engaging customer interactions."
              },
              {
                icon: Phone,
                title: "Automated Outbound Calling",
                description: "Systematically reach out to your customer base to drive service appointments and repeat sales."
              },
              {
                icon: BarChart2,
                title: "Comprehensive Analytics",
                description: "Get detailed insights with call transcriptions, sentiment analysis, and common topic identification."
              },
              {
                icon: MessageSquare,
                title: "Smart Conversation Analysis",
                description: "Understand customer concerns, identify reasons for call endings, and spot communication gaps."
              },
              {
                icon: Shield,
                title: "Enterprise Security",
                description: "Your customer data is protected with bank-level security and encryption."
              },
              {
                icon: Clock,
                title: "24/7 Operation",
                description: "AI agents work around the clock to maximize customer engagement opportunities."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary/30 dark:hover:border-primary-light/30 hover:shadow-lg transition-all group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-feature-gradient opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary-light/10 dark:to-primary-light/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className="text-primary dark:text-primary-light" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-gradient-radial from-gray-100 via-white to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Transform Your Customer Retention
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Let AI handle the follow-ups while you focus on closing deals and growing your business.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative h-[500px] overflow-hidden rounded-lg">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent dark:from-primary-dark/20 dark:to-transparent"></div>
              <img 
                src={images.benefits}
                alt="Auto dealership service center" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-8">
              {[
                {
                  title: "Automate Customer Follow-ups",
                  description: "Let AI agents handle outbound calls while your team focuses on high-value activities."
                },
                {
                  title: "Increase Service Revenue",
                  description: "Proactively engage customers for service appointments and maintenance schedules."
                },
                {
                  title: "Drive Repeat Sales",
                  description: "Identify and nurture opportunities for repeat purchases and referrals."
                }
              ].map((benefit, index) => (
                <div key={index} className="flex gap-4 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary-light/10 dark:to-primary-light/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <ArrowRight className="text-primary dark:text-primary-light" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-12 text-center relative overflow-hidden shadow-xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]"></div>
            <div className="relative">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Modernize Your Customer Outreach?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join forward-thinking dealerships using AI voice agents to drive growth and customer retention.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => handleFormOpen('getStarted')}
                  className="bg-white text-primary px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  Get Started
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => handleFormOpen('demo')}
                  className="bg-transparent text-white border-2 border-white px-8 py-3 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center gap-2"
                >
                  Request Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 dark:bg-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <Logo className="h-8 mb-4" />
              <p className="text-gray-600 dark:text-gray-300">
                AI-powered voice agents for modern auto dealerships.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Features</a></li>
                <li><a href="#benefits" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Benefits</a></li>
                <li>
                  <button
                    onClick={() => handleFormOpen('demo')}
                    className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light"
                  >
                    Request Demo
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">About</a></li>
                <li><a href="#" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Blog</a></li>
                <li>
                  <button
                    onClick={() => handleFormOpen('getStarted')}
                    className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light"
                  >
                    Get Started
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Privacy</a></li>
                <li><a href="#" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Terms</a></li>
                <li><a href="#" className="text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary-light">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8 text-center text-gray-600 dark:text-gray-300">
            <p>© {new Date().getFullYear()} DealerDial.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <ContactForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        formType={formType}
      />
    </div>
  );
}